import React from 'react'

import noop from 'lodash/noop'

import useUniqueId from '../../hooks/use-unique-id'
import cn from '../../libs/class-names'

import invisibleIcon from '../../assets/icons/invisible.svg'
import visibleIcon from '../../assets/icons/visible.svg'

import Icon from '../icon'
import {Props as IconProps} from '../icon/icon'

export interface Props {
  classNames?: string[]
  id?: string
  name?: string
  type?: 'text' | 'password' | 'email' | 'tel'
  icon?: IconProps['source']
  value: string
  error?: boolean
  disabled?: boolean
  prefix?: string
  suffix?: string
  showPassword?: boolean
  autoFocus?: boolean
  autoComplete?: string
  maxLength?: number
  mixed?: boolean
  mixedLabel?: string
  label?: string
  tooltip?: React.ReactElement
  invert?: boolean
  compact?: boolean
  onChange?: (
    value: string,
    name: string | undefined,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
}

export interface InputFieldHandles {
  focus: () => void
}

const InputField = React.forwardRef<InputFieldHandles, Props>(
  (
    {
      id,
      classNames,
      name,
      type = 'text',
      value,
      error = false,
      disabled = false,
      prefix,
      suffix,
      invert = false,
      compact = false,
      showPassword = false,
      mixed = false,
      mixedLabel,
      onChange = noop,
      onBlur = noop,
      onFocus = noop,
      autoFocus,
      autoComplete,
      maxLength,
      label,
      icon,
      tooltip,
    }: Props,
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null)
    const actualId = useUniqueId(id)
    const [passwordVisible, setPasswordVisible] = React.useState(false)
    const [focus, setFocus] = React.useState(false)

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setFocus(true)
      onFocus(event)
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setFocus(false)
      onBlur(event)
    }

    const handleContainerClick = (event: React.MouseEvent<HTMLElement>) => {
      if (
        inputRef.current &&
        !(event.target as HTMLElement).closest('a') &&
        !(event.target as HTMLElement).closest('button')
      ) {
        inputRef.current.focus()
      }
    }

    React.useImperativeHandle(ref, () => ({
      focus: () => {
        if (inputRef.current) {
          inputRef.current.focus()
        }
      },
    }))

    return (
      <div
        className={cn(
          'InputField',
          {
            error,
            disabled,
            focus,
            invert,
            compact,
            empty: !value && !mixed,
            mixed,
            icon: !!icon,
            tooltip: !!tooltip,
            label: !!label,
            [`type-${type}`]: !!type,
          },
          classNames
        )}
        onClick={handleContainerClick}
      >
        {prefix && <div className="InputField__prefix">{prefix}</div>}
        <div className="InputField__main">
          <input
            className="InputField__input"
            name={name}
            id={actualId}
            type={type === 'password' && passwordVisible ? 'text' : type}
            ref={inputRef}
            autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={event => onChange(event.target.value, name, event)}
            value={mixed ? mixedLabel : value}
            disabled={disabled || mixed}
            maxLength={maxLength}
            autoComplete={autoComplete}
          />
          {label && (
            <label htmlFor={actualId} className="InputField__label">
              {label}
            </label>
          )}
        </div>
        {suffix && <div className="InputField__suffix">{suffix}</div>}
        {icon && (
          <div className="InputField__icon">
            <Icon source={icon} />
          </div>
        )}
        {type === 'password' && showPassword && (
          <button
            type="button"
            className="InputField__action"
            onClick={() => setPasswordVisible(!passwordVisible)}
          >
            {passwordVisible ? (
              <Icon block source={invisibleIcon} />
            ) : (
              <Icon block source={visibleIcon} />
            )}
          </button>
        )}
        {tooltip && <div className="InputField__tooltip">{tooltip}</div>}
      </div>
    )
  }
)

InputField.displayName = 'InputField'

export default InputField
